import { useEffect } from 'react';

import { TRecursiveNonNullable } from '@api/Helper.types';
import { useGetContext, useGetProductOffers } from '@api/lendingAPI/lapiHooks';
import formbricks from '@formbricks/js';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { GetProductOffersResponse } from '@iwoca/lapi-client/edge';
import { Card, Link } from '@iwoca/orion';
import { getCurrentPathname } from '@iwRouter/LocationHelper';
import classNames from 'classnames';
import { startCase, upperFirst } from 'lodash';
import { FormattedMessage } from 'react-intl';

import {
    TProductOffersDocRequirementType,
    getOptionalDataRequirements,
    getSuggestedProduct
} from './ApplicationPendingView.helpers';
import styles from './ApplicationPendingView.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import TradingHistory, {
    TGetProductOffersResponseData
} from '../../../ui/documentRequirement/TradingHistory';
import { LoadingAnimation } from '../../../ui/loadingAnimation/LoadingAnimation';
import ApplicationProgressBar from '../../../ui/progressBar/applicationProgressBar/ApplicationProgressBar';
import { PendingView } from '../PendingView';
import { PendingViewActionsDE } from './PendingViewActionsDE/PendingViewActionsDE';

const UK_SUPPORTED_DOCUMENTS: TProductOffersDocRequirementType[] = [
    'business_plan',
    'vat_return',
    'company_accounts',
    'management_accounts',
    'aged_debtor_report'
];

export type TGetProductOffersResponse =
    TRecursiveNonNullable<GetProductOffersResponse>;

type trackingApplicationStatus =
    | 'Application Submitted'
    | 'Documents Review'
    | 'Credit Analyst Review'
    | 'Your Decision';

export const mapProductStatusToStep = (
    status: TGetProductOffersResponse['data']['products'][number]['status']
) => {
    switch (status) {
        case 'accepted':
        case 'unknown':
            return 'applicationSubmitted';
        case 'pending_document_review':
        case 'pending_documents':
            return 'documentsReview';
        case 'credit_assessment_pending':
        case 'credit_assessment_required':
        case 'credit_assessment_amount_less_than_product_key':
            return 'creditAnalystReview';
        default:
            return 'yourDecision';
    }
};

export const ApplicationPendingView = () => {
    const stateKey = useStateKey();
    const {
        data: productOffers,
        isLoading: isLoadingProductOffers,
        isError: isProductOffersError,
        isFetching: isFetchingProductOffers
    } = useGetProductOffers(stateKey);

    if (isLoadingProductOffers && isFetchingProductOffers)
        return (
            <MainSectionWrapper className={styles.wrapper}>
                <Card
                    className={styles.loadingContainer}
                    data-testid="application-loading"
                >
                    <LoadingAnimation />
                    <h2>
                        <FormattedMessage
                            id="_.loading_application.header"
                            defaultMessage="We’re completing your application"
                        />
                    </h2>
                    <p>
                        <FormattedMessage
                            id="_.loading_application.description"
                            defaultMessage="This may take up to 60 seconds"
                        />
                    </p>
                </Card>
            </MainSectionWrapper>
        );

    if (isProductOffersError) return <FallbackErrorModal />;

    const suggestedProduct = getSuggestedProduct(productOffers);

    // A suggested product should always be present in the backend for product_offer endpoint but handling the cases here when it's not loaded in properly.
    if (!suggestedProduct) return <PendingView />;

    return (
        <ApplicationPendingViewContent suggestedProduct={suggestedProduct} />
    );
};

const ApplicationPendingViewContent = ({
    suggestedProduct
}: {
    suggestedProduct: NonNullable<ReturnType<typeof getSuggestedProduct>>;
}) => {
    const { data: contextData } = useGetContext();
    const loggedInUserType = contextData?.logged_in_user_type;

    useEffect(() => {
        if (getCountry() !== 'DE') return;
        if (loggedInUserType !== 'customer') return;
        if (!suggestedProduct.status) return;

        formbricks.setAttribute(
            'product_offers_status',
            suggestedProduct.status
        );

        const timerId = setTimeout(() => {
            formbricks.track('application-pending-view-shown');
        }, 10_000);

        return () => clearTimeout(timerId);
    }, [suggestedProduct.status, loggedInUserType]);

    const optionalDocs: ReturnType<typeof getOptionalDataRequirements> =
        getOptionalDataRequirements(suggestedProduct, UK_SUPPORTED_DOCUMENTS);

    const productOfferStatus = () => {
        return mapProductStatusToStep(
            suggestedProduct.status as TGetProductOffersResponse['data']['products'][number]['status']
        );
    };

    if (productOfferStatus && optionalDocs) {
        const optionalDocsList = optionalDocs.map((doc) => doc.type);

        tracking.ampli.applicationPendingPageViewed({
            'Aged debtor report required': optionalDocsList.includes(
                'aged_debtor_report'
            )
                ? 'Optional'
                : 'Not required',
            'Company accounts required': optionalDocsList.includes(
                'company_accounts'
            )
                ? 'Optional'
                : 'Not required',
            'Management accounts required': optionalDocsList.includes(
                'management_accounts'
            )
                ? 'Optional'
                : 'Not required',
            application_pending_status: startCase(
                upperFirst(productOfferStatus())
            ) as trackingApplicationStatus
        });
    }

    return (
        <MainSectionWrapper className={styles.wrapper}>
            <Card className={classNames(styles.card, styles.progress)}>
                <h2 className={styles.progressHeader}>
                    <FormattedMessage
                        id="_.application_progress.title"
                        defaultMessage="Thanks! We're reviewing your application and will get back to you as soon as possible"
                    />
                </h2>
                {productOfferStatus && (
                    <ApplicationProgressBar appStep={productOfferStatus()} />
                )}
                <p
                    className={styles.progressDescription}
                    data-testid="application-pending-view-description"
                >
                    <FormattedMessage
                        id="_.application_progress.description"
                        defaultMessage="We’ll aim to email you with a decision within 24 hours. If you have any questions about your application don’t hesitate to call us on {phoneNumber}"
                        values={{
                            phoneNumber: (
                                <Link
                                    href={`tel:${
                                        getCountry() === 'DE'
                                            ? '06994323020'
                                            : '020 3778 0105'
                                    }`}
                                    onClick={() =>
                                        tracking.ampli.phoneNumberClicked({
                                            location_clicked_from: `Application progress bar: ${startCase(
                                                upperFirst(productOfferStatus())
                                            )}`,
                                            url: getCurrentPathname()
                                        })
                                    }
                                >
                                    <FormattedMessage
                                        id="_.application_progress.phone_number"
                                        defaultMessage="020 3778 0105"
                                    />
                                </Link>
                            ),
                            // Used in the german translation
                            // eslint-disable-next-line formatjs/enforce-placeholders
                            emailAddress: (
                                <Link href="mailto:support@iwoca.de">
                                    support@iwoca.de
                                </Link>
                            )
                        }}
                    />
                </p>
            </Card>
            {getCountry() === 'UK' &&
                productOfferStatus() === 'documentsReview' &&
                optionalDocs.length > 0 && (
                    <Card className="mt-[15px] p-[25px]">
                        <TradingHistory
                            optionalDocs={
                                optionalDocs as TGetProductOffersResponseData['products'][number]['data_requirements']
                            }
                        />
                    </Card>
                )}
            <DEOnly>
                <PendingViewActionsDE
                    suggestedProductStatus={suggestedProduct.status}
                    productOffersStatus={productOfferStatus()}
                />
            </DEOnly>
        </MainSectionWrapper>
    );
};
